import type { ConversationMessage, PlaceholderAnalysis } from '@/features/AI-documents/types/docGen.types';
import type {
  ChatFlow,
  ChatFlowContext,
  ChatFlowEventResult,
} from '@/features/startTask/preset_task/shared/preset/chatflows/types';
import type { PreSetSelection } from '@/features/startTask/preset_task/shared/preset/types/preset.types';

/**
 * MY — "Change Officer" 카테고리 단일 chatflow.
 *
 * 지원 task code (현재):
 *   - M32105  Appointment of Director  (TMP023~TMP026)
 *
 * 추후 같은 카테고리 다른 task code (Resignation, Change of Particulars 등)도
 * 동일 chatflow + 동일 panel 안에서 docCode/templateName 으로 섹션 토글하여 다룬다 (AGM 패턴).
 *
 * 흐름:
 *   input-title → select-entity → confirm (panel 에서 변수 입력 + Create Task)
 *   ── Person Lookup ──
 *   officer-role → officer-list → officer-detail
 *   ── Address chooser ──
 *   address-list
 */
export const changeOfficerFlow: ChatFlow = {
  id: 'change-officer',
  // 카테고리 매칭 — parentCategory 또는 category 이름에 'change officer' 포함 시 활성화.
  categoryMatch: ['change officer'],

  getInitialMessages(preSet: PreSetSelection): ConversationMessage[] {
    const docCount = preSet.documents.length;
    return [
      {
        role: 'ai',
        content:
          `You selected **${preSet.task.TASK_NAME}** (Change Officer).\n\n` +
          `This preset includes ${docCount} document(s).\n\n` +
          `First, please enter a **Task Title**.`,
        timestamp: Date.now(),
        stepId: 1,
        source: 'system',
      },
    ];
  },

  steps: [
    {
      id: 'input-title',
      aiMessage: 'Please enter a Task Title.',
      widget: 'text-input',
      widgetConfig: {
        targetVarKey: '_taskTitle',
        targetEntityIndex: 0,
      },
      awaitEvent: true,
    },
    {
      id: 'select-entity',
      aiMessage: 'Search and select the **entity** for this Change Officer task.',
      widget: 'entity-selector',
      widgetConfig: {
        targetVarKey: '_entity',
        targetEntityIndex: 0,
        analysis: {
          success: true,
          placeholder: '_entity',
          sourceResolver: 'ENTITY',
          officerRole: null,
          varKey: '_entity',
          varLabel: 'Entity',
          confidence: 1,
          message: 'Select Entity (Change Officer).',
          relatedFields: [],
          options: [],
        } as PlaceholderAnalysis,
      },
      awaitEvent: true,
    },
    {
      id: 'confirm',
      aiMessage:
        'Entity linked. Please fill the officer details on the right panel and click **Create Task** when ready.',
      widget: null,
      nextStep: null,
    },
    // ── Person Lookup steps ──
    {
      id: 'officer-role',
      aiMessage: 'Please select a role for the person you want to look up.',
      widget: 'officer-role-buttons',
      awaitEvent: true,
    },
    {
      id: 'officer-list',
      aiMessage: 'Select a person from the list.',
      widget: 'officer-list',
      awaitEvent: true,
    },
    {
      id: 'officer-detail',
      aiMessage:
        'Person details loaded. The form has been pre-filled. You can also drag/click the fields below.',
      widget: 'officer-field-picker',
      awaitEvent: true,
    },
    // ── Address chooser ──
    {
      id: 'address-list',
      aiMessage: 'This person has multiple addresses. Pick the one to use.',
      widget: 'address-options',
      awaitEvent: true,
    },
  ],

  handleEvent(eventName: string, payload: any, context: ChatFlowContext): ChatFlowEventResult {
    // 1) Task Title
    if (eventName === 'value-submit' && context.currentStepId === 'input-title') {
      const title = typeof payload === 'string' ? payload : payload?.value || payload;
      if (!title || !String(title).trim()) return {};
      return {
        messages: [
          {
            role: 'user',
            content: String(title),
            timestamp: Date.now(),
            stepId: 1,
          },
        ],
        formUpdates: { taskTitle: String(title).trim() },
        nextStepId: 'select-entity',
      };
    }

    // 2) Entity 선택
    if (eventName === 'entity-select' && context.currentStepId === 'select-entity') {
      const ent = payload || {};
      const entityCd = ent.entityCd || ent.icpCd || '';
      const entityName = ent.entityName || ent.companyName || 'Entity';
      if (!entityCd) return {};
      return {
        messages: [
          {
            role: 'user',
            content: `Selected: **${entityName}**`,
            timestamp: Date.now(),
            stepId: 1,
          },
        ],
        formUpdates: {
          entityCd,
          entityName,
          icpCd: entityCd,
          registrationNo: ent.registrationNo || '',
        },
        nextStepId: 'confirm',
      };
    }

    // 3) Panel 의 AI 아이콘 클릭 → 사람 lookup 진입
    if (eventName === 'request-person-lookup') {
      const ctx = payload || {};
      const entityCd = context.entityCd || context.formData.entityCd || '';
      if (!entityCd) {
        return {
          messages: [
            {
              role: 'ai',
              content: 'Please select an entity first.',
              timestamp: Date.now(),
              stepId: 1,
              source: 'system',
            },
          ],
        };
      }
      if (ctx.defaultRole) {
        return {
          messages: [
            {
              role: 'user',
              content: `Looking up **${ctx.fieldGroup}** (role: ${ctx.defaultRole})...`,
              timestamp: Date.now(),
              stepId: 1,
            },
          ],
          nextStepId: 'officer-list',
          nextStepWidgetData: {
            entityCd,
            selectedRole: ctx.defaultRole,
          },
        };
      }
      return {
        messages: [
          {
            role: 'user',
            content: `Looking up **${ctx.fieldGroup}**...`,
            timestamp: Date.now(),
            stepId: 1,
          },
        ],
        nextStepId: 'officer-role',
      };
    }

    // 4) Officer Role 선택
    if (eventName === 'officer-role-select' && context.currentStepId === 'officer-role') {
      const role = payload;
      const entityCd = context.entityCd || context.formData.entityCd || '';
      return {
        messages: [
          {
            role: 'user',
            content: `Role: **${role}**`,
            timestamp: Date.now(),
            stepId: 1,
          },
        ],
        nextStepId: 'officer-list',
        nextStepWidgetData: { entityCd, selectedRole: role },
      };
    }

    // 5) Officer 선택
    if (eventName === 'officer-select' && context.currentStepId === 'officer-list') {
      const officer = payload || {};
      const officerName = officer.name || officer.contactName || 'Selected person';
      return {
        messages: [
          {
            role: 'user',
            content: `Selected person: **${officerName}**`,
            timestamp: Date.now(),
            stepId: 1,
          },
        ],
        nextStepId: 'officer-detail',
      };
    }

    // 6) officer-list 뒤로가기
    if (eventName === 'officer-list-back' && context.currentStepId === 'officer-list') {
      return { nextStepId: 'officer-role' };
    }

    // 7) Address lookup 요청
    if (eventName === 'request-address-lookup') {
      const ctx = payload || {};
      const opts = Array.isArray(ctx.addresses) ? ctx.addresses : [];
      const subject = ctx.subjectLabel || 'this person';
      return {
        messages: [
          {
            role: 'user',
            content: `Choose an address for **${subject}**.`,
            timestamp: Date.now(),
            stepId: 1,
          },
        ],
        nextStepId: 'address-list',
        nextStepWidgetData: { addressOptions: opts as any },
      };
    }

    // 8) address option 선택
    if (eventName === 'address-option-select') {
      const opt = payload || {};
      return {
        messages: [
          {
            role: 'user',
            content: `Selected: **${opt.label || opt.type}**`,
            timestamp: Date.now(),
            stepId: 1,
          },
        ],
        nextStepId: 'confirm',
      };
    }

    return {};
  },
};
